import type { AuthResponse, LoginRequest, RegisterRequest, User, Profile, Assessment, SubmitResponseRequest, AssessmentHistory } from '../types';
import type { Question, DetailedAssessmentResult as AssessmentResults } from '../types/assessment.types';

const API_BASE_URL = '/api/v2';

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle all 401 Unauthorized errors (token expired, invalid, missing, etc.)
      if (response.status === 401) {
        // Skip auth handling for login/register endpoints to avoid infinite loops
        if (!endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
          this.handleUnauthorized();
          throw new Error('UNAUTHORIZED');
        }
      }

      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  private handleUnauthorized(): void {
    // Clear token and user data
    this.token = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    
    // Save current URL to redirect back after login
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('redirect_after_login', currentPath);
    
    // Redirect to login page
    window.location.href = '/login';
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    this.token = response.token;
    localStorage.setItem('auth_token', response.token);
    
    return response;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    this.token = response.token;
    localStorage.setItem('auth_token', response.token);
    
    return response;
  }

  async getProfile(): Promise<User> {
    return this.request<User>('/auth/me');
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  // Content methods
  async getContentProfiles(): Promise<Profile[]> {
    // Como esta é uma rota pública com um prefixo diferente,
    // fazemos a chamada diretamente para o endpoint absoluto para evitar
    // a adição automática do base path /api/v2.
    const response = await fetch('/api/public/v1/content/profiles');
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch profiles:', response.status, errorText);
      throw new Error(`Failed to fetch profiles: ${response.statusText}`);
    }
    const data = await response.json();
    return data.profiles; // Extrair array de perfis da resposta
  }

  // Profile methods
  async getProfiles(): Promise<Profile[]> {
    return this.request<Profile[]>('/profiles');
  }

  // Assessment methods
  async getQuestions(): Promise<{ questions: Question[]; total: number }> {
    return this.request<{ questions: Question[]; total: number }>('/assessments/communication/questions');
  }

  async startAssessment(): Promise<Assessment> {
    return this.request<Assessment>('/assessments/communication/start', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async submitResponse(assessmentId: string, response: SubmitResponseRequest): Promise<Assessment> {
    return this.request<Assessment>(`/assessments/communication/${assessmentId}/submit`, {
      method: 'POST',
      body: JSON.stringify(response),
    });
  }

  async completeAssessment(assessmentId: string): Promise<Assessment> {
    return this.request<Assessment>(`/assessments/communication/${assessmentId}/finalize`, {
      method: 'POST',
    });
  }

  async getResults(assessmentId: string): Promise<{ assessment_id: string; results: AssessmentResults; generated_at: string }> {
    return this.request<{ assessment_id: string; results: AssessmentResults; generated_at: string }>(`/assessments/communication/${assessmentId}/results`);
  }

  // Assessment history methods
  async getAssessmentHistory(): Promise<AssessmentHistory> {
    return this.request<AssessmentHistory>('/assessments/communication/history');
  }

  async getAssessmentResult(assessmentId: string): Promise<AssessmentResults> {
    return this.request<AssessmentResults>(`/assessments/communication/${assessmentId}/result`);
  }

  // Dev methods
  async simulateResult(profile: string, tier: 'free' | 'premium'): Promise<AssessmentResults> {
    return this.request<AssessmentResults>(`/dev/simulate-result?profile=${profile}&tier=${tier}`);
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }
}

export const apiService = new ApiService();